import React, { useRef, useState, useCallback, memo, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import SignaturePad from "./SignaturePad";
import { useSignaturePad } from "../hooks/useSignaturePad";

const formatAssignmentDate = (assignment) => {
  const ts =
    assignment?.firstAssignment?.timestamp?.toDate?.() ??
    assignment?.firstAssignment?.timestamp ??
    assignment?.assignedAt;
  if (!ts) return "-";
  const d = ts.seconds != null ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString("he-IL");
};

const PersonnelConfirmModal = ({ isOpen, onClose, onConfirm, assignment }) => {
  const personnelSignaturePadRef = useRef(null);
  const [hasPersonnelSignature, setHasPersonnelSignature] = useState(false);

  const { clearSignature, getSignatureData } = useSignaturePad(
    personnelSignaturePadRef,
    setHasPersonnelSignature,
    null,
    personnelSignaturePadRef,
    null,
    setHasPersonnelSignature,
  );

  useEffect(() => {
    if (!isOpen) return;
    setHasPersonnelSignature(false);
    const canvas = personnelSignaturePadRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [isOpen, assignment?.id]);

  const handleConfirm = useCallback(async () => {
    if (!personnelSignaturePadRef?.current) {
      alert("נא להוסיף חתימה לאישור ההקצאה");
      return;
    }

    // Simple and reliable signature validation
    if (!hasPersonnelSignature) {
      // Double-check by looking at canvas content
      const canvas = personnelSignaturePadRef.current;
      const ctx = canvas.getContext("2d");
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Check if there are any non-transparent pixels
      let hasContent = false;
      for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] > 0) {
          hasContent = true;
          break;
        }
      }

      if (!hasContent) {
        alert("נא להוסיף חתימה לאישור ההקצאה");
        return;
      }
    }

    const personnelSignatureUrl = getSignatureData(personnelSignaturePadRef);
    await onConfirm(personnelSignatureUrl);
  }, [
    personnelSignaturePadRef,
    hasPersonnelSignature,
    getSignatureData,
    onConfirm,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
    clearSignature(personnelSignaturePadRef);
  }, [onClose, clearSignature, personnelSignaturePadRef]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div
        className="modal-content signature-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>אישור הקצאה על ידי החייל</h3>
          <button type="button" className="modal-close" onClick={handleCancel}>
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          <div className="signature-confirmation">
            <div className="personnel-confirmation-info">
              <h4>הקצאה ממתינה לאישור</h4>
              {assignment?.id && (
                <p>
                  <strong>מספר הקצאה:</strong> #{assignment.id.slice(-8)}
                </p>
              )}
              <p>
                <strong>מ.א:</strong> {assignment?.personnelId ?? "-"}
              </p>
              <p>
                <strong>הוקצה על ידי:</strong>{" "}
                {assignment?.firstAssignment?.assignedByName ?? "-"}
              </p>
              <p>
                <strong>תאריך:</strong> {formatAssignmentDate(assignment)}
              </p>
              <p>
                <strong>פריטים:</strong> {assignment?.items?.length ?? 0}
              </p>
              <h4>אני מאשר שקיבלתי את הפריטים המוחתמים</h4>
              <p>
                על ידי חתימה זו אני מאשר שקיבלתי את כל הפריטים המוחתמים לי
                ומתחייב לשמור עליהם.
              </p>
            </div>

            <div className="signature-section">
              <label>חתימה דיגיטלית:</label>
              <div className="signature-pad-container">
                <SignaturePad
                  canvasRef={personnelSignaturePadRef}
                  onSignatureChange={setHasPersonnelSignature}
                  personnelSignaturePadRef={personnelSignaturePadRef}
                  setHasPersonnelSignature={setHasPersonnelSignature}
                  width={400}
                  height={150}
                />
                <div className="signature-pad-actions">
                  <button
                    type="button"
                    className="clear-signature-btn"
                    onClick={() => clearSignature(personnelSignaturePadRef)}
                  >
                    נקה חתימה
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            ביטול
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!hasPersonnelSignature}
          >
            אישר קבלת פריטים
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(PersonnelConfirmModal);
